import React, { useState, useEffect } from "react";
import { useRef } from "react";
import "./LifeInsurance.css";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch, useSelector } from "react-redux";
import {
  submitEnquiryForm,
  resetEnquiryFormState,
} from "../../store/slices/enquiryFormSlice";
import { toast } from "react-toastify";

const LifeInsurance = () => {
  const [formData, setFormData] = useState({
    fullname: "",
    phone: "",
    email: "",
    referral_name: "",
    referral_email: "",
    message: "", // Text area for life insurance information
  });

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitStatus, setSubmitStatus] = useState("");
  const [recaptchaToken, setCaptchaValue] = useState(null);
  const [errors, setErrors] = useState({});
  const [openAccordion, setOpenAccordion] = useState([
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
  ]);
  const recaptchaRef = useRef(null);

  // Calculator state
  const [calculatorData, setCalculatorData] = useState({
    annualIncome: "",
    existingInvestments: "",
    outstandingLoans: "",
    dependents: "1",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCalculatorChange = (e) => {
    const { name, value } = e.target;
    setCalculatorData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRecaptchaChange = (value) => {
    setCaptchaValue(value);

    if (errors.captcha) {
      setErrors((prev) => ({
        ...prev,
        captcha: "",
      }));
    }
  };

  const toggleAccordion = (index) => {
    setOpenAccordion((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index],
    );
  };

  const calculateCoverage = () => {
    const income = parseFloat(calculatorData.annualIncome) || 0;
    const investments = parseFloat(calculatorData.existingInvestments) || 0;
    const loans = parseFloat(calculatorData.outstandingLoans) || 0;
    const dependents = parseInt(calculatorData.dependents) || 1;

    // Human Life Value (HLV) based calculation

    let baseMultiplier = 15;

    // Dependents  according multiplier increase
    if (dependents === 1) baseMultiplier = 15;
    else if (dependents === 2) baseMultiplier = 18;
    else if (dependents === 3) baseMultiplier = 20;
    else if (dependents === 4) baseMultiplier = 22;
    else baseMultiplier = 25; // 5+ dependents

    // Calculate coverage
    let coverage = income * baseMultiplier + loans - investments;

    if (coverage < 500000) coverage = 500000; // Minimum ₹5 Lakh

    // Format the result
    if (coverage >= 10000000)
      return `₹ ${(coverage / 10000000).toFixed(2)} Crore`;
    if (coverage >= 100000) return `₹ ${(coverage / 100000).toFixed(2)} Lakh`;
    return `₹ ${coverage.toLocaleString()}`;
  };
  // Format backend errors
  const formatBackendErrors = (errors) => {
    if (!errors || typeof errors !== "object")
      return "An error occurred. Please try again.";

    return Object.entries(errors)
      .flatMap(([field, messages]) =>
        messages.map(
          (m) =>
            `${field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}: ${m}`,
        ),
      )
      .join("\n");
  };

  // Form validation
  const validateForm = () => {
    const phoneRegex = /^[0-9]{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.fullname || !formData.email || !formData.phone)
      return "Please fill in all required fields";

    if (!phoneRegex.test(formData.phone))
      return "Please enter a valid 10-digit mobile number";

    if (!emailRegex.test(formData.email))
      return "Please enter a valid email address";

    if (formData.referral_email && !emailRegex.test(formData.referral_email))
      return "Please enter a valid referral email address";

    if (!acceptTerms) return "Please accept the terms and conditions";
    if (!recaptchaToken) {
      return "Please verify captcha";
    }

    return null;
  };

  // Clear message after timeout
  const clearMessageAfterTimeout = () => {
    setTimeout(() => {
      setSubmitMessage("");
      setSubmitStatus("");
    }, 5000);
  };

  const dispatch = useDispatch();
  const { success, error, message } = useSelector((state) => state.enquiryForm);

  const handleSubmit = (e) => {
    e.preventDefault();

    const errorMsg = validateForm();
    if (errorMsg) {
      setSubmitMessage(errorMsg);
      setSubmitStatus("error");
      clearMessageAfterTimeout();
      return;
    }
    if (!recaptchaToken) {
      toast.error("Please verify that you are human");
      return;
    }
    setIsSubmitting(true);
    setSubmitMessage("");

    const payload = {
      fullname: formData.fullname.trim(),
      email: formData.email.trim().toLowerCase(),
      mobile: formData.phone.trim(),

      require_service: "Life Insurance",
      page_url: window.location.href,
      recaptcha_token: recaptchaToken,

      ...(formData.referral_name && {
        reference_name: formData.referral_name.trim(),
      }),
      ...(formData.referral_email && {
        reference_email: formData.referral_email.trim().toLowerCase(),
      }),
      ...(formData.message && {
        additional_data: JSON.stringify({
          message: formData.message.trim(),
        }),
      }),
    };

    dispatch(submitEnquiryForm(payload));
  };
  useEffect(() => {
    if (success) {
      setSubmitMessage("Form submitted successfully ✅");
      setSubmitStatus("success");

      setFormData({
        fullname: "",
        phone: "",
        email: "",
        referral_name: "",
        referral_email: "",
        message: "",
      });

      setAcceptTerms(false);

      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      clearMessageAfterTimeout();
      setIsSubmitting(false);

      dispatch(resetEnquiryFormState());
    }
  }, [success, message]); // ✅ add message
  useEffect(() => {
    if (error) {
      setSubmitMessage(error);
      setSubmitStatus("error");

      clearMessageAfterTimeout();
      setIsSubmitting(false);
    }
  }, [error]);
  const accordionData = [
    {
      id: 1,
      title: "What is Life Insurance?",
      content: [
        "Life Insurance is a contract between an insurer and a policyholder. The insurer promises to pay a designated beneficiary a sum of money in exchange for a premium, upon the death of the insured person or after a set period.",
        "It provides financial protection to your family in case of your untimely demise. It ensures that your loved ones don't face financial hardship and can maintain their standard of living even in your absence.",
        "Life insurance also serves as a long-term investment tool, helping you build wealth while securing your family's future.",
      ],
    },
    {
      id: 2,
      title: "Types of Life Insurance Plans",
      types: [
        {
          name: "Term Insurance",
          description:
            "Pure protection plan with high coverage at low premiums. Ideal for young earners.",
          features: [
            "High coverage",
            "Low premium",
            "No maturity benefit",
            "Tax benefits",
          ],
        },
        {
          name: "Whole Life Insurance",
          description:
            "Coverage for entire lifetime with maturity benefits. Perfect for long-term planning.",
          features: [
            "Lifetime coverage",
            "Maturity benefit",
            "Cash value accumulation",
            "Loan facility",
          ],
        },
        {
          name: "Endowment Plans",
          description:
            "Savings-oriented plans that offer both protection and wealth creation.",
          features: [
            "Maturity benefits",
            "Bonuses",
            "Savings component",
            "Tax savings",
          ],
        },
        {
          name: "ULIP",
          description:
            "Market-linked plans combining insurance with investment in equities or debt.",
          features: [
            "Market returns",
            "Investment choice",
            "Switching options",
            "Lock-in period",
          ],
        },
        {
          name: "Money Back Policy",
          description:
            "Regular payouts during policy term for meeting financial goals.",
          features: [
            "Periodic payouts",
            "Survival benefits",
            "Bonus additions",
            "Death cover",
          ],
        },
      ],
    },
    {
      id: 3,
      title: "Top Life Insurance Companies in India",
      companies: [
        {
          name: "LIC of India",
          plans: ["Jeevan Anand", "Jeevan Labh", "Tech Term"],
          claimRatio: "98.31%",
          rating: "★★★★★",
        },
        {
          name: "HDFC Life",
          plans: ["Click 2 Protect", "Sanchay Plus", "YoungStar ULP"],
          claimRatio: "98.07%",
          rating: "★★★★★",
        },
        {
          name: "ICICI Prudential",
          plans: ["iProtect Smart", "Pension Plans", "Wealth Builder"],
          claimRatio: "97.89%",
          rating: "★★★★½",
        },
        {
          name: "SBI Life",
          plans: ["eShield", "Smart Samriddhi", "Retail ULIP"],
          claimRatio: "97.52%",
          rating: "★★★★½",
        },
        {
          name: "Max Life",
          plans: ["Term Plan", "Whole Life", "Savings Plan"],
          claimRatio: "98.42%",
          rating: "★★★★★",
        },
      ],
    },
    {
      id: 4,
      title: "How Much Life Insurance Do You Need?",
      methods: [
        {
          method: "Human Life Value (HLV) Method",
          formula: "Annual Income × Number of Earning Years",
          example: "₹10L × 30 years = ₹3 Crore coverage",
        },
        {
          method: "Expense Replacement Method",
          formula: "Annual Expenses × 20",
          example: "₹6L × 20 = ₹1.2 Crore coverage",
        },
        {
          method: "Goal-Based Method",
          formula: "Future Goals + Liabilities - Existing Assets",
          example: "₹50L + ₹30L - ₹10L = ₹70L coverage",
        },
      ],
    },
    {
      id: 5,
      title: "Tax Benefits Under Life Insurance",
      benefits: [
        {
          section: "Section 80C",
          benefit: "Premium paid up to ₹1.5 lakhs deductible",
          eligibility: "Self, spouse, children",
        },
        {
          section: "Section 10(10D)",
          benefit: "Maturity proceeds tax-free",
          eligibility: "Premium ≤ 10% of sum assured",
        },
        {
          section: "Section 80CCD(1B)",
          benefit: "Additional ₹50,000 for NPS",
          eligibility: "NPS subscribers only",
        },
      ],
    },
    {
      id: 6,
      title: "Term Insurance vs Traditional Plans",
      comparison: {
        headers: ["Feature", "Term Insurance", "Traditional Plans"],
        rows: [
          ["Premium", "Very Low", "High"],
          ["Coverage", "High (10-20X income)", "Moderate"],
          ["Returns", "No returns on survival", "Guaranteed returns"],
          ["Investment", "Pure protection", "Savings + protection"],
          ["Flexibility", "High", "Limited"],
          ["Tax Benefits", "Section 80C", "Section 80C + 10(10D)"],
        ],
      },
    },
    {
      id: 7,
      title: "Riders and Add-ons",
      riders: [
        {
          name: "Critical Illness Rider",
          benefit: "Lump sum on diagnosis of 30+ critical illnesses",
          cost: "Additional 15-25% premium",
        },
        {
          name: "Accident Death Benefit",
          benefit: "Double coverage in case of accidental death",
          cost: "Additional 5-10% premium",
        },
        {
          name: "Waiver of Premium",
          benefit: "Future premiums waived on disability",
          cost: "Additional 10-15% premium",
        },
        {
          name: "Income Benefit Rider",
          benefit: "Monthly income to family after policyholder's death",
          cost: "Additional 20-30% premium",
        },
      ],
    },
    {
      id: 8,
      title: "Claim Settlement Ratio of Top Insurers",
      ratios: [
        { company: "Max Life Insurance", ratio: "98.42%", year: "2023-24" },
        { company: "HDFC Life Insurance", ratio: "98.07%", year: "2023-24" },
        { company: "LIC of India", ratio: "98.31%", year: "2023-24" },
        { company: "ICICI Prudential", ratio: "97.89%", year: "2023-24" },
        { company: "SBI Life Insurance", ratio: "97.52%", year: "2023-24" },
        { company: "Tata AIA Life", ratio: "97.25%", year: "2023-24" },
      ],
    },
    {
      id: 9,
      title: "Common Exclusions in Life Insurance",
      exclusions: [
        "Suicide within first year of policy",
        "Death due to hazardous activities",
        "Death under influence of alcohol/drugs",
        "Pre-existing diseases (in health riders)",
        "War or terrorist activities",
        "Participation in criminal activities",
      ],
    },
    {
      id: 10,
      title: "How to Choose the Right Life Insurance?",
      tips: [
        "Assess your financial goals and family needs",
        "Calculate required coverage using HLV method",
        "Compare premiums and features across insurers",
        "Check claim settlement ratio of insurance companies",
        "Read policy documents carefully for exclusions",
        "Consider adding riders for comprehensive coverage",
        "Choose policy term based on your retirement age",
        "Verify the insurer's solvency margin",
        "Look for online purchase discounts",
        "Review portability options before buying",
      ],
    },
  ];

  return (
    <div className="life-insurance-wrapper">
      {/* Hero Section */}
      <section className="li-hero-section">
        <div className="li-container">
          <div className="li-hero-content">
            <div className="li-hero-text">
              <span className="li-badge">Secure Your Family's Future</span>
              <h1 className="li-main-title">
                <span className="li-title-line">Life</span>
                <span className="li-title-line accent">Insurance</span>
              </h1>
              <p className="li-hero-description">
                Protect your loved ones with comprehensive life insurance
                coverage. Choose from term plans, ULIPs, endowment policies, and
                more. Get financial security with tax benefits under Section
                80C.
              </p>
              <div className="li-cta-group">
                <button className="li-btn li-btn-primary">
                  Calculate Coverage
                </button>
                <button className="li-btn li-btn-outline">Compare Plans</button>
              </div>
              <div className="li-stats">
                <div className="li-stat-item">
                  <span className="li-stat-value">₹1 Cr+</span>
                  <span className="li-stat-label">Coverage</span>
                </div>
                <div className="li-stat-item">
                  <span className="li-stat-value">24+</span>
                  <span className="li-stat-label">Insurers</span>
                </div>
                <div className="li-stat-item">
                  <span className="li-stat-value">98%</span>
                  <span className="li-stat-label">Claim Ratio</span>
                </div>
              </div>
            </div>
            <div className="li-hero-visual">
              <div className="li-image-wrapper">
                <img
                  src="/images/insurance/life.png"
                  alt="Life Insurance Protection"
                  className="li-main-image"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="li-shape-divider">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="li-shape-fill"
            ></path>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="li-features-section">
        <div className="li-container">
          <div className="li-section-header centered">
            <span className="li-section-tag">Why Choose Us</span>
            <h2 className="li-section-title">Comprehensive Life Coverage</h2>
            <p className="li-section-desc">
              Choose from multiple plans tailored to your life stage and
              financial goals
            </p>
          </div>

          <div className="li-features-grid">
            <div className="li-feature-card">
              <div className="li-feature-icon protection-icon">🛡️</div>
              <h3>Financial Security</h3>
              <p>
                Ensure your family's financial independence even in your absence
              </p>
            </div>
            <div className="li-feature-card">
              <div className="li-feature-icon tax-icon">💰</div>
              <h3>Tax Benefits</h3>
              <p>Save up to ₹1.5 lakhs under Section 80C and 10(10D)</p>
            </div>
            <div className="li-feature-card">
              <div className="li-feature-icon investment-icon">📊</div>
              <h3>Wealth Creation</h3>
              <p>ULIPs and endowment plans help build long-term wealth</p>
            </div>
            <div className="li-feature-card">
              <div className="li-feature-icon flexible-icon">⚡</div>
              <h3>Flexible Plans</h3>
              <p>Choose from term, whole life, money-back, and ULIP options</p>
            </div>
            <div className="li-feature-card">
              <div className="li-feature-icon claim-icon">✓</div>
              <h3>High Claim Ratio</h3>
              <p>98%+ claim settlement ratio across top insurers</p>
            </div>
            <div className="li-feature-card">
              <div className="li-feature-icon riders-icon">➕</div>
              <h3>Additional Riders</h3>
              <p>Critical illness, accident, and premium waiver options</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Form */}
      <section className="li-main-section">
        <div className="li-container">
          <div className="li-main-grid">
            {/* Left Column - Benefits & Calculator */}
            <div className="li-benefits-column">
              <div className="li-calculator-card">
                <h3>Quick Coverage Calculator</h3>
                <p>Estimate your life insurance needs in seconds</p>

                <div className="li-calculator-form">
                  <div className="li-calc-row">
                    <label>Annual Income (₹)</label>
                    <input
                      type="number"
                      name="annualIncome"
                      value={calculatorData.annualIncome}
                      onChange={handleCalculatorChange}
                      placeholder="₹ 10,00,000"
                    />
                  </div>
                  <div className="li-calc-row">
                    <label>Existing Investments (₹)</label>
                    <input
                      type="number"
                      name="existingInvestments"
                      value={calculatorData.existingInvestments}
                      onChange={handleCalculatorChange}
                      placeholder="₹ 5,00,000"
                    />
                  </div>
                  <div className="li-calc-row">
                    <label>Outstanding Loans (₹)</label>
                    <input
                      type="number"
                      name="outstandingLoans"
                      value={calculatorData.outstandingLoans}
                      onChange={handleCalculatorChange}
                      placeholder="₹ 20,00,000"
                    />
                  </div>
                  <div className="li-calc-row">
                    <label>Number of Dependents</label>
                    <select
                      name="dependents"
                      value={calculatorData.dependents}
                      onChange={handleCalculatorChange}
                    >
                      <option value="0">0</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5+</option>
                    </select>
                  </div>
                </div>

                <div className="li-calc-result">
                  <span>Recommended Coverage</span>
                  <strong>{calculateCoverage()}</strong>
                </div>

                <button className="li-calc-btn">Get Detailed Analysis</button>
              </div>

              <div className="li-testimonial-card">
                <div className="li-testimonial-quote">"</div>
                <p className="li-testimonial-text">
                  My family received the claim amount within 7 days of filing.
                  The support team was incredibly helpful during difficult
                  times.
                </p>
                <div className="li-testimonial-author">
                  <div className="li-author-avatar">PK</div>
                  <div>
                    <h4>Priya Kapoor</h4>
                    <span>Beneficiary, Term Plan</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form with reCAPTCHA */}
            <div className="li-form-column">
              <div className="li-form-card">
                <div className="li-form-header">
                  <h3>Get Free Quotes</h3>
                  <p>Compare plans from top insurers instantly</p>
                </div>

                {/* Submit Message Display */}
                {submitMessage && (
                  <div className={`li-submit-message ${submitStatus}`}>
                    <span className="li-message-icon">
                      {submitStatus === "success" ? "✓" : "⚠️"}
                    </span>
                    {submitMessage}
                    <button
                      className="li-close-message"
                      onClick={() => {
                        setSubmitMessage("");
                        setSubmitStatus("");
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                <form className="li-form" onSubmit={handleSubmit}>
                  {/* Full Name */}
                  <div className="li-input-group full-width">
                    <label>
                      Full Name <span className="li-required">*</span>
                    </label>
                    <input
                      type="text"
                      name="fullname"
                      value={formData.fullname}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="li-input-group full-width">
                    <label>
                      Phone Number <span className="li-required">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="9737304440"
                      required
                      pattern="[0-9]{10}"
                      title="Please enter exactly 10 digits"
                    />
                  </div>

                  {/* Email */}
                  <div className="li-input-group full-width">
                    <label>
                      Email Address <span className="li-required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="loansviakrishna@gmail.com
"
                      required
                    />
                  </div>

                  {/* Referral Name */}
                  <div className="li-input-group full-width">
                    <label>Referral Name (Optional)</label>
                    <input
                      type="text"
                      name="referral_name"
                      value={formData.referral_name}
                      onChange={handleInputChange}
                      placeholder="Who referred you?"
                    />
                  </div>

                  {/* Referral Email */}
                  <div className="li-input-group full-width">
                    <label>Referral Email (Optional)</label>
                    <input
                      type="email"
                      name="referral_email"
                      value={formData.referral_email}
                      onChange={handleInputChange}
                      placeholder="Referrer's email address"
                    />
                  </div>

                  {/* Message Text Area */}
                  <div className="li-input-group full-width">
                    <label>Tell us about your Life Insurance needs</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Please share any specific requirements or questions about life insurance..."
                      rows="4"
                      className="li-textarea"
                    />
                  </div>

                  {/* reCAPTCHA */}
                  <div className="li-recaptcha">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6Ldk_IksAAAAAO8Aq02xgOPeBlszLT2K30hdJCjM"
                      onChange={handleRecaptchaChange}
                      // ✅ ADD THIS (important)
                      onExpired={() => {
                        setCaptchaValue(null);
                        setErrors((prev) => ({
                          ...prev,
                          captcha: "Captcha expired, please verify again",
                        }));
                      }}
                      // ✅ OPTIONAL (network issue handle)
                      onErrored={() => {
                        setCaptchaValue(null);
                        setErrors((prev) => ({
                          ...prev,
                          captcha: "Captcha failed to load. Try again.",
                        }));
                      }}
                    />
                  </div>

                  {/* Terms and Conditions */}
                  <div className="li-terms-group">
                    <input
                      type="checkbox"
                      id="li-terms"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <label htmlFor="li-terms">
                      I agree to receive loan updates and promotional from{" "}
                      <strong>Krishana Finconcept</strong> via WhatsApp and
                      other accept the{" "}
                      <a
                        href="/privacy-security-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms & Conditions
                      </a>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="li-submit-btn"
                    disabled={!acceptTerms || isSubmitting || !recaptchaToken}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="li-spinner"></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Get Free Quotes
                        <span className="li-btn-arrow">→</span>
                      </>
                    )}
                  </button>

                  <p className="li-form-footer">
                    *No spam. 100% privacy guaranteed
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Partners Section */}
      <section className="li-partners-section">
        <div className="li-container">
          <h2 className="li-partners-title">
            Trusted by India's Leading Insurers
          </h2>
          <div className="li-partners-marquee">
            <div className="li-partners-track">
              {[
                "lic",
                "hdfc",
                "icici",
                "sbi",
                "max",
                "tata",
                "bajaj",
                "kotak",
              ].map((partner, index) => (
                <div key={index} className="li-partner-item">
                  <img
                    src={`/bank-logos/${partner}.png`}
                    alt={`${partner} logo`}
                  />
                </div>
              ))}
              {/* Duplicate for infinite scroll */}
              {[
                "lic",
                "hdfc",
                "icici",
                "sbi",
                "max",
                "tata",
                "bajaj",
                "kotak",
              ].map((partner, index) => (
                <div key={`dup-${index}`} className="li-partner-item">
                  <img
                    src={`/bank-logos/${partner}.png`}
                    alt={`${partner} logo`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Accordion Section */}
      <section className="li-accordion-section">
        <div className="li-container">
          <div className="li-section-header centered">
            <span className="li-section-tag">Knowledge Center</span>
            <h2 className="li-section-title">
              Everything About Life Insurance
            </h2>
            <p className="li-section-desc">
              Comprehensive guides to help you make informed decisions
            </p>
          </div>

          <div className="li-accordion-container">
            {accordionData.map((item) => (
              <div
                key={item.id}
                className={`li-accordion-item ${openAccordion.includes(item.id) ? "active" : ""}`}
              >
                <div
                  className="li-accordion-header"
                  onClick={() => toggleAccordion(item.id)}
                >
                  <h3>{item.title}</h3>
                  <span className="li-accordion-icon">
                    {openAccordion.includes(item.id) ? "−" : "+"}
                  </span>
                </div>
                <div className="li-accordion-content">
                  {item.content && (
                    <div className="li-content-text">
                      {item.content.map((para, idx) => (
                        <p key={idx}>{para}</p>
                      ))}
                    </div>
                  )}

                  {item.types && (
                    <div className="li-types-grid">
                      {item.types.map((type, idx) => (
                        <div key={idx} className="li-type-card">
                          <h4>{type.name}</h4>
                          <p>{type.description}</p>
                          <ul>
                            {type.features.map((feature, fidx) => (
                              <li key={fidx}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {item.companies && (
                    <div className="li-companies-table">
                      {item.companies.map((company, idx) => (
                        <div key={idx} className="li-company-row">
                          <div className="li-company-info">
                            <h4>{company.name}</h4>
                            <div className="li-company-rating">
                              {company.rating}
                            </div>
                          </div>
                          <div className="li-company-plans">
                            {company.plans.map((plan, pidx) => (
                              <span key={pidx} className="li-plan-tag">
                                {plan}
                              </span>
                            ))}
                          </div>
                          <div className="li-company-ratio">
                            <span>Claim Ratio: {company.claimRatio}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {item.methods && (
                    <div className="li-methods-grid">
                      {item.methods.map((method, idx) => (
                        <div key={idx} className="li-method-card">
                          <h4>{method.method}</h4>
                          <p className="li-formula">{method.formula}</p>
                          <p className="li-example">{method.example}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {item.benefits && (
                    <div className="li-benefits-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Section</th>
                            <th>Benefit</th>
                            <th>Eligibility</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.benefits.map((benefit, idx) => (
                            <tr key={idx}>
                              <td>
                                <strong>{benefit.section}</strong>
                              </td>
                              <td>{benefit.benefit}</td>
                              <td>{benefit.eligibility}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {item.comparison && (
                    <div className="li-table-wrapper">
                      <table className="li-comparison-table">
                        <thead>
                          <tr>
                            {item.comparison.headers.map((header, idx) => (
                              <th key={idx}>{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {item.comparison.rows.map((row, ridx) => (
                            <tr key={ridx}>
                              {row.map((cell, cidx) => (
                                <td key={cidx}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {item.riders && (
                    <div className="li-riders-grid">
                      {item.riders.map((rider, idx) => (
                        <div key={idx} className="li-rider-card">
                          <h4>{rider.name}</h4>
                          <p className="li-rider-benefit">{rider.benefit}</p>
                          <p className="li-rider-cost">{rider.cost}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {item.ratios && (
                    <div className="li-ratios-grid">
                      {item.ratios.map((item, idx) => (
                        <div key={idx} className="li-ratio-item">
                          <span className="li-company-name">
                            {item.company}
                          </span>
                          <span className="li-ratio-value">{item.ratio}</span>
                          <span className="li-ratio-year">{item.year}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {item.exclusions && (
                    <ul className="li-exclusions-list">
                      {item.exclusions.map((exclusion, idx) => (
                        <li key={idx}>{exclusion}</li>
                      ))}
                    </ul>
                  )}

                  {item.tips && (
                    <ol className="li-tips-list">
                      {item.tips.map((tip, idx) => (
                        <li key={idx}>{tip}</li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LifeInsurance;
